import { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { Skeleton } from '@/web/components/ui/skeleton';
import { useCreatorAuth } from '../hooks/useCreatorAuth';

/**
 * The gate in front of every portal screen.
 *
 * ── What this is and, more importantly, what it is not ─────────────────────
 *
 * It is NOT the security boundary. `requireOnboarded` on the Express router is
 * — it refuses the portal's own routes outright, so a creator mid-onboarding
 * gets no data regardless of what the browser renders. If this component were
 * the only guard, the portal would be one devtools request away and any state
 * lost on refresh would take the gate with it.
 *
 * What this IS: the reason a blocked creator sees their next step instead of an
 * error. The server refuses; this decides where they go instead.
 *
 * ── Why the redirect is keyed on the server's answer ───────────────────────
 *
 * `onboarding.step` comes from `onboardingState()` in @shared/creator-portal —
 * the same function the middleware calls. Two independent notions of "done"
 * would produce the one bug this flow cannot tolerate: a creator bounced
 * forever between the portal and an onboarding screen because the server thinks
 * they are finished and the client does not.
 */
export function RequireOnboarded({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated, onboarding } = useCreatorAuth();
  const location = useLocation();

  if (isLoading) return <LoadingShell />;

  if (!isAuthenticated) {
    // Carry where they were going, so signing in resumes it rather than
    // dumping them on the default tab.
    return <Navigate to={`/creator/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  }

  if (onboarding && !onboarding.isComplete) {
    /*
      `create_account` is unreachable here by construction — no password means
      no session, and no session means the branch above already fired. It is
      still listed rather than defaulted, because a silent fallthrough on a step
      nobody expected is how a creator ends up staring at an empty portal.
    */
    if (onboarding.step === 'watch_video') return <Navigate to="/creator/watch-video" replace />;
    // The contract list is the first onboarding screen, not a redirect target for itself.
    if (onboarding.step === 'sign_contract' && location.pathname === '/creator/contracts') {
      return <>{children}</>;
    }
    return <Navigate to="/creator/contracts" replace />;
  }

  return <>{children}</>;
}

/**
 * The gate for the onboarding screens themselves — the mirror image.
 *
 * Without it, a creator who has finished can navigate back to /creator/book-call
 * and rebook a call they have already had, or sit on a screen that no longer
 * applies to them. Sends anyone already through the flow to the portal.
 */
export function RequireOnboarding({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated, isOnboarded, onboarding } = useCreatorAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) navigate('/creator/login', { replace: true });
  }, [isLoading, isAuthenticated, navigate]);

  if (isLoading) return <LoadingShell />;
  if (isOnboarded) return <Navigate to="/creator/videos" replace />;
  if (onboarding?.step === 'sign_contract' && !/^\/creator\/contracts\/[^/]+\/sign$/.test(location.pathname)) {
    return <Navigate to="/creator/contracts" replace />;
  }

  return <>{children}</>;
}

/**
 * A skeleton, not a spinner.
 *
 * This renders on every cold load while `/auth/me` resolves, and it is the
 * first paint of the app. A skeleton in roughly the shape of the shell reads as
 * "loading"; a centred spinner on a blank page reads as "broken", which is a
 * poor first impression for an audience we are asking to trust us with their
 * income.
 */
function LoadingShell() {
  return (
    <div className="bg-background flex min-h-screen">
      <div className="border-border bg-card flex w-56 shrink-0 flex-col gap-2 border-r p-3">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
      <div className="flex flex-1 flex-col gap-3 p-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
      </div>
    </div>
  );
}
